/**
 * Access to the configured `$api` instance, plus the streaming escape hatch.
 *
 * `apiFetch` IS `$api` — see `app/plugins/api.ts`. Its path is a plain `string` and it does not look
 * the route up in Nuxt's route map (MAR-1131), so every call states its result type:
 * `apiFetch<Thing[]>(path)`, with `Thing` imported from `#shared/<domain>/<name>.ts` (the type the
 * service function is annotated with). An unannotated call returns `unknown`.
 *
 * USE `useApi().apiFetch` IN NEW CODE, NEVER BARE `$fetch` (apps/web/AGENTS.md, "API and plumbing"; the "Code
 * organization conventions (web)" page in Notion Tech, §6, MAR-948). Bare `$fetch` skips the
 * plugin's interceptors — auth, the org override and 503 tracking. An earlier
 * version of this comment said the opposite; it was wrong.
 *
 * The one exception is `app/utils/sessionCookie.ts`, whose session-cookie handshake runs before
 * `$api` has a token to attach, and so calls bare `$fetch` on purpose. `app/plugins/api.ts` builds
 * `$api` with `$fetch.create`, which is construction, not a call.
 */
import { ORG_OVERRIDE_HEADER } from '#shared/organizations/orgOverride'
import { getStoredOrgOverride } from '~/utils/orgOverride'
import { APP_CHECK_HEADER_NAME, appCheckTokenKindFor } from '#shared/appCheck'
import { appCheckTokenFor } from '~/utils/appCheckToken'

export function useApi() {
  const nuxtApp = useNuxtApp()
  const { $api } = nuxtApp
  const { recordFailure, recordSuccess } = useServiceStatus()

  /**
   * Streaming responses, which never go through `$api`.
   *
   * It returns the raw `Response` so the caller can read `body` as a stream, so there is no parsed
   * payload to type and no route lookup to preserve. It builds its own headers for the same reason:
   * ofetch's interceptors never run for it.
   */
  async function apiStreamFetch(path: string, opts: RequestInit = {}): Promise<Response> {
    const headers = new Headers(opts.headers)

    if (import.meta.server) {
      const { cookie } = useRequestHeaders(['cookie'])
      if (cookie) headers.set('cookie', cookie)
    } else {
      if (!headers.has('Authorization')) {
        const { getToken } = useAuth()
        const token = await getToken()
        if (token) headers.set('Authorization', `Bearer ${token}`)
      }
      // Same rule as `app/plugins/api.ts` (MAR-1214): a token only for the gated routes, so a
      // streaming chat never initializes App Check.
      const tokenKind = appCheckTokenKindFor(opts.method, path)
      if (tokenKind && !headers.has(APP_CHECK_HEADER_NAME)) {
        const appCheckToken = await appCheckTokenFor(nuxtApp, tokenKind)
        if (appCheckToken) headers.set(APP_CHECK_HEADER_NAME, appCheckToken)
      }
      // Kept in step with `app/plugins/api.ts` on purpose: a streaming chat request that dropped the
      // override would silently answer from the super-admin's OWN organization while the banner
      // said otherwise (MAR-427).
      const orgOverride = getStoredOrgOverride()
      if (orgOverride) headers.set(ORG_OVERRIDE_HEADER, orgOverride)
    }

    const response = await fetch(path, { ...opts, headers })
    if (response.ok) recordSuccess()
    else if (response.status === 503) recordFailure()
    return response
  }

  return { apiFetch: $api, apiStreamFetch }
}

/**
 * The type a composable's deps declare for `apiFetch` — `$api`'s own (`Api` in `app/plugins/api.ts`),
 * never a narrowed copy.
 */
export type ApiFetch = ReturnType<typeof useApi>['apiFetch']
